// Service for the Mi Asistencia dashboard
import { apiClient, API_CONFIG } from '@/api/core';
import { buildQueryString } from '@/api/shared';
import type { ApiResponse } from '@/api/shared';
import type {
  LC_GetCurrentWeekOut,
  LC_GetDailyStatusOut,
  LC_TodayMarkItem,
  LC_DashboardMonthlyOut,
  LC_DashboardMonthlyIn,
  LC_GetMonthlyCompaniesSummaryOut,
  AttendanceDashboard,
  DashboardMark,
  DashboardMonthly,
  DashboardWeekly,
  MonthlyCompanyCutoff,
} from '../types';
import { mapCurrentWeek, mapDashboard, mapDashboardMonthly, mapMonthlyCompaniesSummary, mapTodayMarks } from '../mappers';


export class AttendanceDashboardService {
  async getDashboard(signal?: AbortSignal): Promise<ApiResponse<AttendanceDashboard>> {
    const endpoint = API_CONFIG.ENDPOINTS.ATTENDANCE.DASHBOARD();
    const response = await apiClient.get<LC_GetDailyStatusOut>(endpoint, { signal });

    if (!response.success || !response.data) {
      return { success: false, data: null, error: response.error, status: response.status };
    }
    const today = new Date();
    return { success: true, data: mapDashboard(response.data, today), status: response.status };
  }

  async getTodayMarks(signal?: AbortSignal): Promise<ApiResponse<DashboardMark[]>> {
    const endpoint = API_CONFIG.ENDPOINTS.ATTENDANCE.TODAY_MARKS();
    const response = await apiClient.get<LC_TodayMarkItem[]>(endpoint, { signal });

    if (!response.success || !response.data) {
      return { success: false, data: null, error: response.error, status: response.status };
    }
    return { success: true, data: mapTodayMarks(response.data), status: response.status };
  }

  async getCurrentWeek(signal?: AbortSignal): Promise<ApiResponse<DashboardWeekly>> {
    const endpoint = API_CONFIG.ENDPOINTS.ATTENDANCE.CURRENT_WEEK();
    const response = await apiClient.get<LC_GetCurrentWeekOut>(endpoint, { signal });

    if (!response.success || !response.data) {
      return { success: false, data: null, error: response.error, status: response.status };
    }
    return { success: true, data: mapCurrentWeek(response.data), status: response.status };
  }

  async getMonthlySummary(
    input: LC_DashboardMonthlyIn,
  ): Promise<ApiResponse<DashboardMonthly>> {
    const queryString = buildQueryString({
      year: input.year,
      month: input.month,
      monthlyCutId: input.monthlyCutId,
      cutoffCompanyId: input.cutoffCompanyId,
    });
    const endpoint = `${API_CONFIG.ENDPOINTS.ATTENDANCE.DASHBOARD_MONTHLY_RESUME()}${queryString}`;
    const response = await apiClient.get<LC_DashboardMonthlyOut>(endpoint);

    if (!response.success || !response.data) {
      return { success: false, data: null, error: response.error, status: response.status };
    }
    return { success: true, data: mapDashboardMonthly(response.data), status: response.status };
  }

  async getMonthlyCompaniesSummary(
    signal?: AbortSignal,
  ): Promise<ApiResponse<MonthlyCompanyCutoff[]>> {
    const endpoint = API_CONFIG.ENDPOINTS.ATTENDANCE.DASHBOARD_MONTHLY_COMPANIES_SUMMARY();
    const response = await apiClient.get<LC_GetMonthlyCompaniesSummaryOut>(endpoint, { signal });

    if (!response.success || !response.data) {
      return { success: false, data: null, error: response.error, status: response.status };
    }
    return {
      success: true,
      data: mapMonthlyCompaniesSummary(response.data),
      status: response.status,
    };
  }
}

export const attendanceDashboardService = new AttendanceDashboardService();
